import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import EmptyState from '../../components/common/EmptyState';
import './BugReportsViewer.css';

function BugReportsViewer() {
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bugReports, setBugReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIsOpen, setSelectedIsOpen] = useState('true');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedResolutionStatus, setSelectedResolutionStatus] = useState('unaddressed');
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [updatingReport, setUpdatingReport] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    fetchBugReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedIsOpen, selectedSeverity, selectedResolutionStatus, auth.user?.id_token]);

  const fetchBugReports = async () => {
    if (!auth.isAuthenticated || !auth.user?.id_token) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      const params = {
        limit: ITEMS_PER_PAGE,
        offset
      };

      if (selectedIsOpen !== 'all') {
        params.is_open = selectedIsOpen;
      }

      if (selectedSeverity !== 'all') {
        params.severity = selectedSeverity;
      }

      // Only include resolution_status if user is a developer
      if (isDeveloper && selectedResolutionStatus !== 'all') {
        params.resolution_status = selectedResolutionStatus;
      }

      const data = await apiService.getBugReports(auth.user.id_token, params);
      setBugReports(data.bugReports);
      setTotal(data.total);
      setIsDeveloper(data.isDeveloper || false);
    } catch (err) {
      console.error('Error fetching bug reports:', err);
      setError(err.message || 'Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityClass = (severity) => {
    const classes = {
      low: 'severity-low',
      medium: 'severity-medium',
      high: 'severity-high',
      critical: 'severity-critical'
    };
    return classes[severity] || 'severity-medium';
  };

  const getResolutionStatusClass = (resolutionStatus) => {
    const classes = {
      unaddressed: 'resolution-unaddressed',
      resolved: 'resolution-resolved',
      rejected: 'resolution-rejected'
    };
    return classes[resolutionStatus] || 'resolution-unaddressed';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setSelectedReport(null);
    }
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
    setSelectedReport(null);
  };

  const handleUpdateResolution = async (resolutionStatus) => {
    if (!selectedReport || !isDeveloper) return;

    // Check if closed
    if (!selectedReport.is_open) {
      alert('Cannot modify a closed bug report.');
      return;
    }

    // Require note for resolved/rejected statuses
    if ((resolutionStatus === 'resolved' || resolutionStatus === 'rejected') && !resolutionNote.trim()) {
      alert('Please provide a note when marking a report as resolved or rejected.');
      return;
    }

    setUpdatingReport(true);
    try {
      await apiService.updateBugReportResolution(
        auth.user.id_token,
        selectedReport.report_id,
        resolutionStatus,
        resolutionNote.trim() || null
      );

      // Refresh the bug reports list
      await fetchBugReports();

      // Close modal and reset state
      setSelectedReport(null);
      setResolutionNote('');
    } catch (err) {
      console.error('Error updating bug report:', err);
      alert(err.message || 'Failed to update bug report');
    } finally {
      setUpdatingReport(false);
    }
  };

  const handleCloseBugReport = async () => {
    if (!selectedReport || !isDeveloper) return;

    if (!selectedReport.is_open) {
      alert('Bug report is already closed.');
      return;
    }

    if (!window.confirm('Are you sure you want to close this bug report? Closed reports cannot be modified.')) {
      return;
    }

    setUpdatingReport(true);
    try {
      await apiService.closeBugReport(auth.user.id_token, selectedReport.report_id);
      await fetchBugReports();
      setSelectedReport(null);
      setResolutionNote('');
    } catch (err) {
      console.error('Error closing bug report:', err);
      alert(err.message || 'Failed to close bug report');
    } finally {
      setUpdatingReport(false);
    }
  };

  const handleReopenBugReport = async () => {
    if (!selectedReport || !isDeveloper) return;

    if (selectedReport.is_open) {
      alert('Bug report is already open.');
      return;
    }

    setUpdatingReport(true);
    try {
      await apiService.reopenBugReport(auth.user.id_token, selectedReport.report_id);
      await fetchBugReports();
      setSelectedReport(null);
      setResolutionNote('');
    } catch (err) {
      console.error('Error reopening bug report:', err);
      alert(err.message || 'Failed to reopen bug report');
    } finally {
      setUpdatingReport(false);
    }
  };

  const handleDeleteBugReport = async () => {
    if (!selectedReport || !isDeveloper) return;

    setUpdatingReport(true);
    try {
      await apiService.deleteBugReport(auth.user.id_token, selectedReport.report_id);
      await fetchBugReports();
      setSelectedReport(null);
      setShowDeleteConfirm(false);
      setResolutionNote('');
    } catch (err) {
      console.error('Error deleting bug report:', err);
      alert(err.message || 'Failed to delete bug report');
    } finally {
      setUpdatingReport(false);
    }
  };

  if (loading && bugReports.length === 0) {
    return <LoadingSpinner message="Loading bug reports..." />;
  }

  if (error && bugReports.length === 0) {
    return (
      <ErrorDisplay
        title="Error Loading Bug Reports"
        message={error}
        onRetry={fetchBugReports}
      />
    );
  }

  return (
    <div className="bug-reports-viewer">
      <div className="bug-reports-header">
        <h1>Bug Reports</h1>
        <div className="bug-reports-stats">
          <span className="total-count">{total} total reports</span>
        </div>
      </div>

      <div className="bug-reports-filters">
        <div className="filter-group">
          <label htmlFor="is-open-filter">Status:</label>
          <select
            id="is-open-filter"
            value={selectedIsOpen}
            onChange={(e) => {
              setSelectedIsOpen(e.target.value);
              handleFilterChange();
            }}
          >
            <option value="all">All</option>
            <option value="true">Open</option>
            <option value="false">Closed</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="severity-filter">Severity:</label>
          <select
            id="severity-filter"
            value={selectedSeverity}
            onChange={(e) => {
              setSelectedSeverity(e.target.value);
              handleFilterChange();
            }}
          >
            <option value="all">All Severities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        {isDeveloper && (
          <div className="filter-group">
            <label htmlFor="resolution-filter">Resolution:</label>
            <select
              id="resolution-filter"
              value={selectedResolutionStatus}
              onChange={(e) => {
                setSelectedResolutionStatus(e.target.value);
                handleFilterChange();
              }}
            >
              <option value="all">All Resolutions</option>
              <option value="unaddressed">Unaddressed</option>
              <option value="resolved">Resolved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        )}
      </div>

      {bugReports.length === 0 ? (
        <EmptyState
          icon="🐛"
          message={
            selectedIsOpen !== 'all' || selectedSeverity !== 'all' || selectedResolutionStatus !== 'all'
              ? 'No bug reports match the selected filters'
              : 'No bug reports found'
          }
        />
      ) : (
        <>
          <div className="bug-reports-list">
            {bugReports.map((report) => (
              <div
                key={report.report_id}
                className={`bug-report-card ${
                  selectedReport?.report_id === report.report_id ? 'selected' : ''
                }`}
                onClick={() => setSelectedReport(report)}
              >
                <div className="bug-report-header">
                  <h3 className="bug-report-title">{report.title}</h3>
                  <div className="bug-report-badges">
                    <span className={`severity-badge ${getSeverityClass(report.severity)}`}>
                      {report.severity}
                    </span>
                    <span className={`status-badge ${report.is_open ? 'status-open' : 'status-closed'}`}>
                      {report.is_open ? 'Open' : 'Closed'}
                    </span>
                    {isDeveloper && report.resolution_status && (
                      <span className={`resolution-badge ${getResolutionStatusClass(report.resolution_status)}`}>
                        {report.resolution_status}
                      </span>
                    )}
                  </div>
                </div>

                <p className="bug-report-description">{report.description}</p>

                <div className="bug-report-meta">
                  <span className="reporter">
                    Reported by: <strong>{report.display_name}</strong>
                  </span>
                  <span className="date">{formatDate(report.created_at)}</span>
                </div>

                {report.page_url && (
                  <div className="bug-report-url">
                    <span className="url-label">Page:</span>
                    <a
                      href={report.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {report.page_url}
                    </a>
                  </div>
                )}

                {report.developer_note && (
                  <div className="developer-comment-card">
                    <div className="developer-comment-header">
                      <span className="developer-icon">💬</span>
                      <strong>Developer Comment</strong>
                      {report.resolved_by_name && (
                        <span className="developer-name">by {report.resolved_by_name}</span>
                      )}
                    </div>
                    <p className="developer-comment-text">{report.developer_note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="pagination-info">
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <span className="pagination-range">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
                  {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total}
                </span>
              </div>

              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedReport && (
        <div className="bug-report-modal" onClick={() => setSelectedReport(null)}>
          <div className="bug-report-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedReport(null)}>
              ×
            </button>

            <div className="modal-header">
              <h2>{selectedReport.title}</h2>
              <div className="report-id-badge">ID: {selectedReport.report_id}</div>
              <div className="modal-badges">
                <span className={`severity-badge ${getSeverityClass(selectedReport.severity)}`}>
                  {selectedReport.severity}
                </span>
                <span className={`status-badge ${selectedReport.is_open ? 'status-open' : 'status-closed'}`}>
                  {selectedReport.is_open ? 'Open' : 'Closed'}
                </span>
                {isDeveloper && selectedReport.resolution_status && (
                  <span className={`resolution-badge ${getResolutionStatusClass(selectedReport.resolution_status)}`}>
                    {selectedReport.resolution_status}
                  </span>
                )}
              </div>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3>Description</h3>
                <p>{selectedReport.description}</p>
              </div>

              {selectedReport.steps_to_reproduce && (
                <div className="modal-section">
                  <h3>Steps to Reproduce</h3>
                  <p className="preformatted">{selectedReport.steps_to_reproduce}</p>
                </div>
              )}

              {selectedReport.expected_behavior && (
                <div className="modal-section">
                  <h3>Expected Behavior</h3>
                  <p>{selectedReport.expected_behavior}</p>
                </div>
              )}

              {selectedReport.actual_behavior && (
                <div className="modal-section">
                  <h3>Actual Behavior</h3>
                  <p>{selectedReport.actual_behavior}</p>
                </div>
              )}

              <div className="modal-section">
                <h3>Reporter Information</h3>
                <p>
                  <strong>Name:</strong> {selectedReport.display_name}
                  <br />
                  <strong>Email:</strong> {selectedReport.email}
                  <br />
                  <strong>Reported:</strong> {formatDate(selectedReport.created_at)}
                  {selectedReport.resolved_at && (
                    <>
                      <br />
                      <strong>Resolved:</strong> {formatDate(selectedReport.resolved_at)}
                    </>
                  )}
                </p>
              </div>

              {selectedReport.page_url && (
                <div className="modal-section">
                  <h3>Page URL</h3>
                  <a
                    href={selectedReport.page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-link"
                  >
                    {selectedReport.page_url}
                  </a>
                </div>
              )}

              {selectedReport.browser_info && (
                <div className="modal-section">
                  <h3>Browser Information</h3>
                  <p className="browser-info">{selectedReport.browser_info}</p>
                </div>
              )}

              {selectedReport.developer_note && (
                <div className="modal-section">
                  <h3>Developer Note</h3>
                  <p className="developer-note">{selectedReport.developer_note}</p>
                  {selectedReport.resolved_by_name && (
                    <p className="resolved-by">
                      <em>- {selectedReport.resolved_by_name}</em>
                    </p>
                  )}
                </div>
              )}

              {isDeveloper && (
                <div className="modal-section developer-controls">
                  <h3>Developer Actions</h3>

                  {!selectedReport.is_open && (
                    <div className="closed-warning">
                      This bug report is closed and cannot be modified.
                    </div>
                  )}

                  <div className="developer-note-input">
                    <label htmlFor="developer-note">Note:</label>
                    <textarea
                      id="developer-note"
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      placeholder="Add a note explaining your decision..."
                      rows={4}
                      disabled={updatingReport || !selectedReport.is_open}
                    />
                  </div>
                  <div className="developer-buttons">
                    <button
                      className="btn-resolve"
                      onClick={() => handleUpdateResolution('resolved')}
                      disabled={updatingReport || !selectedReport.is_open}
                    >
                      {updatingReport ? 'Updating...' : 'Mark as Resolved'}
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleUpdateResolution('rejected')}
                      disabled={updatingReport || !selectedReport.is_open}
                    >
                      {updatingReport ? 'Updating...' : 'Mark as Rejected'}
                    </button>
                    <button
                      className="btn-unaddressed"
                      onClick={() => handleUpdateResolution('unaddressed')}
                      disabled={updatingReport || !selectedReport.is_open}
                    >
                      {updatingReport ? 'Updating...' : 'Mark as Unaddressed'}
                    </button>
                  </div>

                  <div className="developer-buttons" style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                    {selectedReport.is_open ? (
                      <button
                        className="btn-close-report"
                        onClick={handleCloseBugReport}
                        disabled={updatingReport}
                      >
                        {updatingReport ? 'Closing...' : 'Close Bug Report'}
                      </button>
                    ) : (
                      <button
                        className="btn-reopen-report"
                        onClick={handleReopenBugReport}
                        disabled={updatingReport}
                      >
                        {updatingReport ? 'Reopening...' : 'Reopen Bug Report'}
                      </button>
                    )}
                    <button
                      className="btn-delete-report"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={updatingReport}
                    >
                      Delete Bug Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm-modal" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <span className="delete-warning-icon">⚠️</span>
              <h3>Delete Bug Report?</h3>
            </div>
            <div className="delete-confirm-body">
              <p>
                Are you sure you want to delete this bug report? This action cannot be undone.
              </p>
              <p className="delete-report-info">
                <strong>Report ID:</strong> {selectedReport?.report_id}
                <br />
                <strong>Title:</strong> {selectedReport?.title}
              </p>
            </div>
            <div className="delete-confirm-actions">
              <button
                className="btn-cancel-delete"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={updatingReport}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleDeleteBugReport}
                disabled={updatingReport}
              >
                {updatingReport ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BugReportsViewer;
